import React from 'react';
import { Skeleton } from './skeleton';
import { Card, CardContent } from './card';

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = 4,
  showHeader = true,
  className,
}) => {
  return (
    <div className={className}>
      {/* Header skeleton */}
      {showHeader && (
        <div className="flex items-center space-x-4 p-4 border-b">
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={index} className="h-4 flex-1" />
          ))}
        </div>
      )}
      
      {/* Rows skeleton */}
      <div className="space-y-2 p-4">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex items-center space-x-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={colIndex} className="h-8 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

interface CardSkeletonProps {
  count?: number;
  className?: string;
}

export const CardSkeleton: React.FC<CardSkeletonProps> = ({
  count = 3,
  className,
}) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index}>
          <CardContent className="p-4">
            <div className="space-y-3">
              {/* Title */}
              <Skeleton className="h-5 w-3/4" />
              
              {/* Content lines */}
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
              
              {/* Action buttons */}
              <div className="flex justify-end space-x-2">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-16" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

interface UserTableSkeletonProps {
  rows?: number;
  isMobile?: boolean;
}

export const UserTableSkeleton: React.FC<UserTableSkeletonProps> = ({
  rows = 5,
  isMobile = false,
}) => {
  if (isMobile) {
    return <CardSkeleton count={rows} />;
  }

  return (
    <TableSkeleton
      rows={rows}
      columns={6} // email, status, roles, lastLogin, createdAt, actions
      showHeader={true}
    />
  );
};

interface RoleTableSkeletonProps {
  rows?: number;
  isMobile?: boolean;
}

export const RoleTableSkeleton: React.FC<RoleTableSkeletonProps> = ({
  rows = 5,
  isMobile = false,
}) => {
  if (isMobile) {
    return <CardSkeleton count={rows} />;
  }

  return (
    <TableSkeleton
      rows={rows}
      columns={6} // name, description, service, permissions, createdAt, actions
      showHeader={true}
    />
  );
};